/**
 * Recent Searches — client-side, per-browser search history for the
 * Dawn header search field. Not synced to a customer account.
 *
 * Storage: localStorage, key below, JSON array of strings, most recent first.
 */
class RecentSearches {
  static STORAGE_KEY = 'dawn:recent-searches';
  static MAX_TERMS = 5;

  static getAll() {
    try {
      const raw = window.localStorage.getItem(RecentSearches.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((t) => typeof t === 'string') : [];
    } catch (error) {
      // Corrupt JSON or localStorage unavailable (private mode, disabled, quota) — fail soft.
      return [];
    }
  }

  static add(term) {
    const trimmed = (term || '').trim();
    if (!trimmed) return;

    let terms = RecentSearches.getAll();
    // De-dupe case-insensitively; re-searching a term should move it to the top, not duplicate it.
    terms = terms.filter((t) => t.toLowerCase() !== trimmed.toLowerCase());
    terms.unshift(trimmed);
    terms = terms.slice(0, RecentSearches.MAX_TERMS);

    try {
      window.localStorage.setItem(RecentSearches.STORAGE_KEY, JSON.stringify(terms));
    } catch (error) {
      // Quota exceeded or storage disabled — nothing to recover, just don't persist.
    }
  }

  static clear() {
    try {
      window.localStorage.removeItem(RecentSearches.STORAGE_KEY);
    } catch (error) {
      /* no-op */
    }
  }
}

class RecentSearchesList extends HTMLElement {
  connectedCallback() {
    this.input = this.getScopedInput();

    if (!this.input) return;

    this.onFocus = this.handleFocus.bind(this);
    this.onInput = this.handleFocus.bind(this);
    this.onSubmit = this.handleSubmit.bind(this);
    this.onClick = this.handleClick.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);

    this.input.addEventListener('focus', this.onFocus);
    this.input.addEventListener('input', this.onInput);
    this.form = this.input.closest('form');
    this.form?.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    document.addEventListener('click', this.onDocumentClick);
    }

    getScopedInput() {
        let scope = this.parentElement;
        while (scope) {
            const input = scope.querySelector('.predictive-search-input');
            if (input) return input;
            scope = scope.parentElement;
        }
        // Fallback: no shared ancestor found, grab the first one on the page.
        return document.querySelector('.predictive-search-input');
    }

  disconnectedCallback() {
    this.input?.removeEventListener('focus', this.onFocus);
    this.input?.removeEventListener('input', this.onInput);
    this.form?.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('click', this.onDocumentClick);
  }

  handleFocus() {
    // AC1: only show the list when the field is focused with no query typed yet.
    if (this.input.value.trim() !== '') {
      this.hide();
      return;
    }
    this.render();
  }

  handleSubmit() {
    RecentSearches.add(this.input.value);
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target) && event.target !== this.input) {
      this.hide();
    }
  }

  handleClick(event) {
    const termButton = event.target.closest('[data-recent-search-term]');
    if (termButton) {
      event.preventDefault();
      const term = termButton.getAttribute('data-recent-search-term');
      this.input.value = term;
      RecentSearches.add(term);

      if (this.form) {
        if (this.form.requestSubmit) {
          this.form.requestSubmit();
        } else {
          this.form.submit();
        }
      }

      this.hide();
      return;
    }

    if (event.target.closest('[data-recent-search-clear]')) {
      event.preventDefault();
      RecentSearches.clear();
      this.render();
    }
  }

  hide() {
    this.hidden = true;
  }

  render() {
    const terms = RecentSearches.getAll();

    if (!terms.length) {
      this.hidden = true;
      this.innerHTML = '';
      return;
    }

    this.hidden = false;
    const title = this.getAttribute('data-title') || 'Recent searches';
    const clearLabel = this.getAttribute('data-clear-label') || 'Clear';

    this.innerHTML = `
      <div class="recent-searches__header flex items-center justify-between">
        <span class="predictive-search__heading text-body caption-with-letter-spacing !mb-0 text-primary 
        font-[var(--font-weight-semibold)] ">${this.escapeHtml(title)}</span>
        <button type="button" class="recent-searches__clear text-primary 
        font-[var(--font-weight-medium)] cursor-pointer" data-recent-search-clear>${this.escapeHtml(clearLabel)}</button>
      </div>
      <ul class="recent-searches__list mt-[12px]" role="list">
        ${terms
          .map(
            (term) => `
          <li class="recent-searches__item  text-primary font-[var(--font-weight-medium)] 
          !text-[length:var(--text-body-sm)] mb-[12px]">
            <button type="button" class="recent-searches__term cursor-pointer" data-recent-search-term="${this.escapeHtml(term)}">
              ${this.escapeHtml(term)}
            </button>
          </li>`
          )
          .join('')}
      </ul>
    `;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

customElements.define('recent-searches-list', RecentSearchesList);